import { useEffect, useState } from "react";

import { X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";

import type { SurveyQuestionDisplay } from "../types";

// AIDEV-NOTE: Single question popup component for quick surveys
// Desktop: fixed bottom-right corner card
// Mobile: full-screen interstitial overlay
// Supports all 4 question types: multiple_choice, multiple_select, short_text, long_text

interface SingleQuestionPopupProps {
    question: SurveyQuestionDisplay;
    onAnswer: (questionId: string, answer: string | string[]) => void;
    onDismiss: () => void;
    // AIDEV-NOTE: Mobile X is a soft close (hide for session, no server dismissal).
    // Tapping X on a full-screen modal is too easy to misinterpret as a permanent
    // dismissal. Desktop corner-card X stays as a real dismissal via `onDismiss`.
    onMobileClose?: () => void;
    isSubmitting?: boolean;
    dismissible?: boolean;
}

export function SingleQuestionPopup({
    question,
    onAnswer,
    onDismiss,
    onMobileClose,
    dismissible = true,
}: SingleQuestionPopupProps) {
    const [selectedValue, setSelectedValue] = useState<string>("");
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [textValue, setTextValue] = useState<string>("");
    const [isLocallySubmitted, setIsLocallySubmitted] = useState(false);

    useEffect(() => {
        setIsLocallySubmitted(false);
        setSelectedValue("");
        setSelectedValues([]);
        setTextValue("");
    }, [question.id]);

    const handleSubmit = () => {
        if (question.questionType === "multiple_choice") {
            if (selectedValue) {
                setIsLocallySubmitted(true);
                onAnswer(question.id, selectedValue);
            }
        } else if (question.questionType === "multiple_select") {
            if (selectedValues.length > 0) {
                setIsLocallySubmitted(true);
                onAnswer(question.id, selectedValues);
            }
        } else if (
            question.questionType === "short_text" ||
            question.questionType === "long_text"
        ) {
            if (textValue.trim()) {
                setIsLocallySubmitted(true);
                onAnswer(question.id, textValue.trim());
            }
        }
    };

    const handleMultiSelectChange = (value: string, checked: boolean) => {
        if (checked) {
            setSelectedValues([...selectedValues, value]);
        } else {
            setSelectedValues(selectedValues.filter((v) => v !== value));
        }
    };

    const isValid =
        question.questionType === "multiple_choice"
            ? !!selectedValue
            : question.questionType === "multiple_select"
              ? selectedValues.length > 0
              : !!textValue.trim();

    if (isLocallySubmitted) {
        return null;
    }

    return (
        <>
            {/* Mobile: Full screen overlay */}
            {/* AIDEV-NOTE: Card is capped at the overlay height (max-h-full) and the
                question body scrolls inside it — without this, long questions (8+
                options) pushed the Submit footer below the fold with no way to
                reach it, since a fixed overlay can't be page-scrolled. */}
            <div
                data-testid="survey-single-popup"
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] lg:hidden"
            >
                <div className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                    <PopupContent
                        question={question}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        selectedValues={selectedValues}
                        handleMultiSelectChange={handleMultiSelectChange}
                        textValue={textValue}
                        setTextValue={setTextValue}
                        onSubmit={handleSubmit}
                        onDismiss={onMobileClose ?? onDismiss}
                        dismissible={dismissible}
                        isValid={isValid}
                    />
                </div>
            </div>

            {/* Desktop: Bottom-right card */}
            <div
                data-testid="survey-single-popup"
                className="fixed bottom-6 right-6 z-50 hidden w-96 lg:block"
            >
                <div className="flex max-h-[calc(100dvh-3rem)] flex-col overflow-hidden rounded-xl border border-brand-forest-200 bg-white shadow-2xl shadow-brand-forest-900/20">
                    <PopupContent
                        question={question}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        selectedValues={selectedValues}
                        handleMultiSelectChange={handleMultiSelectChange}
                        textValue={textValue}
                        setTextValue={setTextValue}
                        onSubmit={handleSubmit}
                        onDismiss={onDismiss}
                        dismissible={dismissible}
                        isValid={isValid}
                    />
                </div>
            </div>
        </>
    );
}

interface PopupContentProps {
    question: SurveyQuestionDisplay;
    selectedValue: string;
    setSelectedValue: (value: string) => void;
    selectedValues: string[];
    handleMultiSelectChange: (value: string, checked: boolean) => void;
    textValue: string;
    setTextValue: (value: string) => void;
    onSubmit: () => void;
    onDismiss: () => void;
    dismissible: boolean;
    isValid: boolean;
}

function PopupContent({
    question,
    selectedValue,
    setSelectedValue,
    selectedValues,
    handleMultiSelectChange,
    textValue,
    setTextValue,
    onSubmit,
    onDismiss,
    dismissible,
    isValid,
}: PopupContentProps) {
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-brand-cream-200 px-5 py-4">
                <p className="text-xs font-medium uppercase tracking-wider text-brand-forest-500">
                    Quick Question
                </p>
                {dismissible && (
                    <button
                        onClick={onDismiss}
                        className="rounded-full p-1.5 text-brand-forest-400 transition-colors hover:bg-brand-cream-100 hover:text-brand-forest-600"
                        aria-label="Dismiss"
                    >
                        <X className="size-5" />
                    </button>
                )}
            </div>

            {/* Question */}
            {/* AIDEV-NOTE: min-h-0 + overflow-y-auto keeps the header/footer pinned
                and scrolls only the question body when the card is height-capped. */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">
                {question.imageSignedUrl && (
                    <div className="mb-4 overflow-hidden rounded-lg">
                        <img
                            src={question.imageSignedUrl}
                            alt=""
                            className="h-auto w-full object-cover"
                        />
                    </div>
                )}

                <p className="mb-4 text-base font-medium text-brand-forest-800">
                    {question.questionText}
                </p>

                {question.questionType === "multiple_choice" &&
                    question.options && (
                        <RadioGroup
                            value={selectedValue}
                            onValueChange={setSelectedValue}
                            className="space-y-2"
                        >
                            {question.options.map((option) => (
                                <div
                                    key={option.value}
                                    className={cn(
                                        "flex items-center space-x-3 rounded-lg border px-4 py-3 transition-colors",
                                        selectedValue === option.value
                                            ? "border-brand-forest-500 bg-brand-forest-50"
                                            : "border-brand-cream-300 hover:border-brand-forest-300 hover:bg-brand-cream-50",
                                    )}
                                >
                                    <RadioGroupItem
                                        value={option.value}
                                        id={option.value}
                                        className="border-brand-forest-400 text-brand-forest-600"
                                    />
                                    <Label
                                        htmlFor={option.value}
                                        className="flex-1 cursor-pointer text-sm font-medium text-brand-forest-700"
                                    >
                                        {option.label}
                                    </Label>
                                </div>
                            ))}
                        </RadioGroup>
                    )}

                {question.questionType === "multiple_select" &&
                    question.options && (
                        <div className="space-y-2">
                            {question.options.map((option) => (
                                <div
                                    key={option.value}
                                    className={cn(
                                        "flex items-center space-x-3 rounded-lg border px-4 py-3 transition-colors",
                                        selectedValues.includes(option.value)
                                            ? "border-brand-forest-500 bg-brand-forest-50"
                                            : "border-brand-cream-300 hover:border-brand-forest-300 hover:bg-brand-cream-50",
                                    )}
                                >
                                    <Checkbox
                                        id={`${question.id}-${option.value}`}
                                        checked={selectedValues.includes(
                                            option.value,
                                        )}
                                        onCheckedChange={(checked) =>
                                            handleMultiSelectChange(
                                                option.value,
                                                checked === true,
                                            )
                                        }
                                        className="border-brand-forest-400 text-brand-forest-600"
                                    />
                                    <Label
                                        htmlFor={`${question.id}-${option.value}`}
                                        className="flex-1 cursor-pointer text-sm font-medium text-brand-forest-700"
                                    >
                                        {option.label}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    )}

                {(question.questionType === "short_text" ||
                    question.questionType === "long_text") && (
                    <Textarea
                        value={textValue}
                        onChange={(e) => setTextValue(e.target.value)}
                        placeholder="Type your answer..."
                        className="min-h-[100px] resize-none"
                        rows={question.questionType === "long_text" ? 4 : 2}
                    />
                )}
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-brand-cream-200 bg-white px-5 py-4">
                <Button
                    type="button"
                    onClick={onSubmit}
                    disabled={!isValid}
                    className="w-full"
                >
                    Submit
                </Button>
            </div>
        </div>
    );
}
